import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, Input, CardMedia } from "@mui/material";
import * as Yup from "yup";
import { isValidInput } from "../utils/validations";
import useTranslation from "../hooks/traslation";
interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  image: string;
  selectedId: string;
  description: string;
  setImage: Dispatch<SetStateAction<string>>;
  setName: Dispatch<SetStateAction<string>>;
  setSelectedId: Dispatch<SetStateAction<string>>;
  setDescription: Dispatch<SetStateAction<string>>;
  setPrice: Dispatch<SetStateAction<string>>;
  price: string;
  handleSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleProfileUpload: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  setDescriptionAr: Dispatch<SetStateAction<string>>;
  setNameAr: Dispatch<SetStateAction<string>>;
  nameAr: string;
  descriptionAr: string;
}

export default function AddCategory({
  open,
  setOpen,
  name,
  setName,
  handleSubmit,
  setImage,
  image,
  handleProfileUpload,
  selectedId,
  setSelectedId,
  description,
  onClose,
  setDescription,
  price,
  setPrice,
  descriptionAr,
  nameAr,
  setDescriptionAr,
  setNameAr,
}: AddCategoryModalProps) {
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    nameAr: "",
    descriptionAr: "",
  });
  const translate = useTranslation();

  const categorySchema = Yup.object().shape({
    name: Yup.string()
      .required(translate?.error?.categoryName)
      .min(2, translate?.error?.catLimit_max)
      .max(50, translate?.error?.catLimit_min),
    nameAr: Yup.string()
      .required(translate?.error?.catName_Arabic)
      .min(2, translate?.error?.minArabicChar_cat),
    description: Yup.string().max(500, translate?.error?.descriptionLimit_max),
    descriptionAr: Yup.string().max(
      500,
      translate?.error?.ArabicDescriptionLimit_max
    ),
  });

  const validateForm = async () => {
    try {
      await categorySchema.validate(
        {
          name,
          nameAr,
          description,
          descriptionAr,
        },
        { abortEarly: false }
      );
      setErrors({ name: "", description: "", nameAr: "", descriptionAr: "" });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = {
          name: "",
          description: "",
          nameAr: "",
          descriptionAr: "",
        };
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof typeof newErrors] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleFormSubmit = async () => {
    if (await validateForm()) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    setSelectedId("");
    setOpen(false);
    setErrors({ name: "", description: "", nameAr: "", descriptionAr: "" });
  };

  return (
    <Modal
      className="modal addCategory_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog w_900">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{selectedId ? "Update Category" : "Add Category"}</h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <form className="form" action="">
            <div className="control_group w_100">
              <label>{translate?.main?.uploadImage}</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image || "/static/images/user_placeholder.png"}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon onClick={() => setImage("")} />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg,image/jpg",
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = null;
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            <div className="gap_p">
              <div className="control_group w_50">
                <label>
                  {translate?.main?.categoryName} {translate?.main?.english}
                </label>
                <TextField
                  placeholder={translate?.user?.enterCategoryName}
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setName(e.target.value);
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  error={!!errors.name}
                  hiddenLabel
                />
                {errors.name && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>
                  {translate?.main?.categoryName} {translate?.main?.arabic}
                </label>
                <TextField
                  placeholder={translate?.user?.enterCategoryName}
                  fullWidth
                  value={nameAr}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setNameAr(e.target.value);
                      setErrors((prev) => ({ ...prev, nameAr: "" }));
                    }
                  }}
                  error={!!errors.nameAr}
                  hiddenLabel
                />
                {errors.nameAr && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.nameAr}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>
                  {translate?.globals?.description} {translate?.main?.english}
                </label>
                <TextField
                  variant="outlined"
                  placeholder={translate?.globals?.description}
                  fullWidth
                  hiddenLabel
                  multiline
                  maxRows={5}
                  minRows={5}
                  value={description}
                  inputProps={{ maxLength: 500 }}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setDescription(e.target.value);
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                  error={!!errors.description}
                />

                {errors.description && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.description}
                  </span>
                )}
              </div>

              <div className="control_group w_50">
                <label>
                  {translate?.globals?.description} {translate?.main?.arabic}
                </label>
                <TextField
                  variant="outlined"
                  placeholder={translate?.globals?.description}
                  fullWidth
                  hiddenLabel
                  multiline
                  maxRows={5}
                  minRows={5}
                  value={descriptionAr}
                  inputProps={{ maxLength: 500 }}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setDescriptionAr(e.target.value);
                      setErrors((prev) => ({ ...prev, descriptionAr: "" }));
                    }
                  }}
                  error={!!errors.descriptionAr}
                />
                {errors.descriptionAr && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.descriptionAr}
                  </span>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button className="btn btn_grey" onClick={handleClose}>
                  {translate?.main?.cancel}
                </Button>
                <Button className="btn btn_primary" onClick={handleFormSubmit}>
                  {selectedId
                    ? translate?.main?.update
                    : translate?.globals?.submit}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
